"use client";

import type { Control } from "react-hook-form";
import type { CreateProfileFormData } from "@/features/profile/tutor/schemas/createProfileSchema";

import { useState } from "react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";

import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore";
import availableLanguages from "@/i18n/availableLanguages";

interface SelectProfileLanguageProps {
  control: Control<CreateProfileFormData>;
  goToNextStep: () => void;
}

export function SelectProfileLanguage({ control }: SelectProfileLanguageProps) {
  const { t } = useTranslation(["common", "profile"]);
  const { settingsData } = useProfileDataStore();
  const [, setSelectedLanguage] = useState<string | null>(null);

  // Filter out already created profile languages
  const availableProfileLanguages = availableLanguages.filter(
    (language) =>
      !settingsData?.main_profile.profiles_locales.find(
        (lang: string) => lang === language.language.toUpperCase(),
      ),
  );

  return (
    <div className="flex h-full w-full flex-col items-center justify-center px-4 py-8 md:px-8">
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-3xl"
        initial={{ opacity: 0, y: 20 }}
        transition={{ duration: 0.5 }}
      >
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
          initial={{ opacity: 0, y: 10 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h2 className="mb-3 text-3xl font-bold tracking-tight">
            {t("profile:getStarted.selectProfileLanguage")}
          </h2>
          <p className="text-muted-foreground text-lg">
            {t("profile:getStarted.languageDescription")}
          </p>
        </motion.div>

        <FormField
          control={control}
          name="locale_code"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <div
                  className={`${availableProfileLanguages.length > 1 ? "grid" : "mx-auto max-w-sm"} gap-4 max-sm:grid-cols-1 grid-cols-${availableProfileLanguages.length > 4 ? 4 : availableProfileLanguages.length}`}
                >
                  {availableProfileLanguages.map((language, index) => (
                    <motion.div
                      key={language.language}
                      animate={{ opacity: 1, scale: 1 }}
                      initial={{ opacity: 0, scale: 0.9 }}
                      transition={{ delay: 0.1 * index, duration: 0.4 }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <button
                        className={`group relative flex h-full w-full flex-col items-center rounded-xl border-2 p-6 transition-all ${
                          field.value === language.language.toUpperCase()
                            ? "border-accent-foreground/75 bg-muted-foreground/5"
                            : "border-border hover:border-accent-foreground/50"
                        }`}
                        type="button"
                        onClick={() => {
                          field.onChange(language.language.toUpperCase());
                          setSelectedLanguage(language.language.toUpperCase());
                        }}
                      >
                        <div className="relative mb-4 aspect-6/5 w-16 overflow-hidden rounded-md">
                          <img
                            alt={language.name}
                            className="h-full w-full object-cover"
                            src={language.flagImg || "/placeholder.svg?height=64&width=64"}
                          />
                        </div>
                        <span className="text-center font-medium">{language.name}</span>

                        {field.value === language.language.toUpperCase() && (
                          <motion.div
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-primary text-primary-foreground absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full"
                            initial={{ opacity: 0, scale: 0.5 }}
                          >
                            <Check className="h-4 w-4" />
                          </motion.div>
                        )}
                      </button>
                    </motion.div>
                  ))}
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      </motion.div>
    </div>
  );
}
